import React from 'react';
import { Modal, Typography, Divider, Row, Col, Space, Spin, Tag } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const PACIFIC_TIMEZONE = 'America/Los_Angeles';

const { Title, Text } = Typography;

/**
 * Modal hiển thị chi tiết payment cho order item
 * Tương tự Transaction Details từ Amazon Seller Central
 */
const PaymentDetailsModal = ({ visible, onCancel, paymentDetails, orderItem, reimbursements = [], loading = false }) => {
    if (!paymentDetails) {
        return null;
    }

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '$0.00';
        const num = parseFloat(amount);
        const sign = num < 0 ? '-' : '';
        return `${sign}$${Math.abs(num).toFixed(2)}`;
    };

    const renderCharges = (charges) => {
        if (!charges) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Product charges</Title>
                {charges.principal && (
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Col>
                            <Text>{orderItem?.productName || orderItem?.title || 'Product'}</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>Qty: {orderItem?.quantityOrdered || 1}</Text>
                        </Col>
                        <Col>
                            <Text strong>{formatCurrency(charges.principal)}</Text>
                        </Col>
                    </Row>
                )}
                
                {/* Other section */}
                {(charges.tax || charges.shipping || charges.shippingTax || charges.giftWrap || charges.giftWrapTax) && (
                    <>
                        <Title level={5} style={{ marginTop: 16, marginBottom: 12 }}>Other</Title>
                        {charges.tax && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Product Tax:</Text></Col>
                                <Col><Text strong>{formatCurrency(charges.tax)}</Text></Col>
                            </Row>
                        )}
                        {charges.shipping && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Shipping:</Text></Col>
                                <Col><Text strong>{formatCurrency(charges.shipping)}</Text></Col>
                            </Row>
                        )}
                        {charges.shippingTax && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Shipping Tax:</Text></Col>
                                <Col><Text strong>{formatCurrency(charges.shippingTax)}</Text></Col>
                            </Row>
                        )}
                        {charges.giftWrap && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Gift Wrap:</Text></Col>
                                <Col><Text strong>{formatCurrency(charges.giftWrap)}</Text></Col>
                            </Row>
                        )}
                        {charges.giftWrapTax && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Col><Text>Gift Wrap Tax:</Text></Col>
                                <Col><Text strong>{formatCurrency(charges.giftWrapTax)}</Text></Col>
                            </Row>
                        )}
                    </>
                )}

                {/* Sales Proceeds */}
                <Divider style={{ margin: '12px 0' }} />
                <Row justify="space-between" style={{ marginBottom: 12 }}>
                    <Col><Title level={5} >Sales Proceeds</Title></Col>
                    <Col><Text strong style={{ color: '#4caf50' }}>{formatCurrency(charges.total)}</Text></Col>
                </Row>
            </div>
        );
    };

    const renderTaxWithheld = (taxWithheld) => {
        if (!taxWithheld || !taxWithheld.total || taxWithheld.total === 0) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Marketplace Withheld Tax</Title>
                {taxWithheld.marketplaceFacilitatorTax && (
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Col><Text>MarketplaceFacilitatorTax-Principal</Text></Col>
                        <Col><Text strong style={{ color: '#d32f2f' }}>{formatCurrency(taxWithheld.marketplaceFacilitatorTax)}</Text></Col>
                    </Row>
                )}
            </div>
        );
    };

    const renderFees = (fees) => {
        if (!fees || Object.keys(fees).length <= 1) return null; // Only 'total' key

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Amazon fees</Title>
                {Object.entries(fees).map(([feeType, amount]) => {
                    if (feeType === 'total') return null;
                    // Explicit zero fulfillment fees remain visible when Amazon reports them.
                    
                    // Format fee type name
                    let displayName = feeType;
                    if (feeType === 'Commission') displayName = 'Referral Fee on Item Price:';
                    else if (feeType === 'ShippingHB') displayName = 'Shipping commission:';
                    else if (feeType === 'FBAPerUnitFulfillmentFee') displayName = 'FBA Per Unit Fulfillment Fee:';
                    
                    const isNegative = amount < 0;
                    
                    return (
                        <Row key={feeType} justify="space-between" style={{ marginBottom: 8 }}>
                            <Col span={12}>
                                <Text>{displayName}</Text>
                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Base amount</Text>
                                {/* Có thể thêm Tax nếu cần */}
                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong style={{ color: isNegative ? '#d32f2f' : '#4caf50' }}>
                                    {formatCurrency(amount)}
                                </Text>
                            </Col>
                        </Row>
                    );
                })}
            </div>
        );
    };

    const renderPromotions = (promotions) => {
        if (!promotions || !promotions.adjustments || promotions.adjustments.length === 0) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Promotions</Title>
                {promotions.adjustments.map((promo, idx) => (
                    <Row key={idx} justify="space-between" style={{ marginBottom: 4 }}>
                        <Col>
                            <Text>{promo.type || 'Promotion'}</Text>
                            {promo.id && <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>({promo.id})</Text>}
                        </Col>
                        <Col><Text strong style={{ color: promo.amount < 0 ? '#d32f2f' : '#4caf50' }}>{formatCurrency(promo.amount)}</Text></Col>
                    </Row>
                ))}
            </div>
        );
    };

    const reimbursementTotal = reimbursements.reduce(
        (sum, allocation) => sum + Number(allocation.amount || 0),
        0
    );
    const accountBalanceChange = Number(paymentDetails.netEarnings || 0) + reimbursementTotal;

    const renderReimbursements = () => {
        if (!loading && reimbursements.length === 0) return null;

        return (
            <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Inventory Reimbursements</Title>
                {loading ? <Spin size="small" /> : reimbursements.map(allocation => {
                    const transaction = allocation.financialTransaction || {};
                    return (
                        <Row key={allocation.id} justify="space-between" style={{ marginBottom: 10 }}>
                            <Col span={17}>
                                <div><Text>{transaction.description || transaction.transactionType}</Text></div>
                                <Space size={6} wrap>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {transaction.postedDate ? dayjs(transaction.postedDate).tz(PACIFIC_TIMEZONE).format('M/D/YYYY') : '-'}
                                    </Text>
                                    {allocation.sellerSKU && <Tag>{allocation.sellerSKU}</Tag>}
                                    {transaction.transactionId && (
                                        <Text type="secondary" style={{ fontSize: 11 }}>{transaction.transactionId}</Text>
                                    )}
                                </Space>
                            </Col>
                            <Col span={7} style={{ textAlign: 'right' }}>
                                <Text strong style={{ color: Number(allocation.amount) < 0 ? '#d32f2f' : '#4caf50' }}>
                                    {formatCurrency(allocation.amount)}
                                </Text>
                            </Col>
                        </Row>
                    );
                })}
                {!loading && reimbursements.length > 1 && (
                    <Row justify="space-between">
                        <Col><Text strong>Total reimbursements</Text></Col>
                        <Col><Text strong>{formatCurrency(reimbursementTotal)}</Text></Col>
                    </Row>
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    <span>Payment Details for Order {orderItem?.order?.amazonOrderId || orderItem?.amazonOrderId}</span>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={700}
        >
            <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: 20, 
                borderRadius: 8 
            }}>
                {/* Header */}
                <div style={{ marginBottom: 16 }}>
                    <Title level={4} style={{ marginBottom: 4 }}>
                        Order Payment for Order {orderItem?.order?.amazonOrderId || orderItem?.amazonOrderId}
                    </Title>
                    <Text type="secondary">
                        <strong>Transaction date:</strong> {paymentDetails.paymentDate ? dayjs(paymentDetails.paymentDate).tz(PACIFIC_TIMEZONE).format('M/D/YYYY') : '—'}
                    </Text>
                </div>

                {/* Product charges & Sales Proceeds */}
                {renderCharges(paymentDetails.charges)}

                {/* Marketplace Withheld Tax */}
                {renderTaxWithheld(paymentDetails.taxWithheld)}

                {/* Amazon Fees */}
                {renderFees(paymentDetails.fees)}

                {/* Promotions */}
                {renderPromotions(paymentDetails.promotions)}

                {renderReimbursements()}

                {/* Final Total */}
                <Divider style={{ margin: '16px 0', borderColor: '#333' }} />
                <Row justify="space-between">
                    <Col>
                        <Text strong style={{ fontSize: 16 }}>Change to your seller account balance</Text>
                    </Col>
                    <Col>
                        <Text strong style={{ fontSize: 16, color: accountBalanceChange < 0 ? '#d32f2f' : '#4caf50' }}>
                            {formatCurrency(accountBalanceChange)}
                        </Text>
                    </Col>
                </Row>
            </div>
        </Modal>
    );
};

export default PaymentDetailsModal;
